import { useMediaQuery, useTheme } from '@mui/material';
import { FeatureGates } from '@services/statsig/constants';
import { useFeatureGate } from '@services/statsig/hooks';
import { useEventTracking } from '@services/statsig/hooks/useEventTracking';

import { JotSupportContainer } from '../components/JotSupportContainer';
import type { JotSupportDisplayProps } from '../components/JotSupportWidget';

import { JotPulseBubble } from './JotPulse';
import { usePulseController } from './usePulseController';
import { usePulseReader } from './usePulseReader';
import { usePulseTransport } from './usePulseTransport';

/** Mounted only inside the existing feature_jot_customer_support branch. */
export function JotSupportWithPulse({
  identityKey,
  isIdentityCurrent,
  ...display
}: JotSupportDisplayProps & {
  replyEmail: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  identityKey: string;
  isIdentityCurrent: () => boolean;
}) {
  const gate = useFeatureGate(FeatureGates.PULSE);
  const desktop = useMediaQuery(useTheme().breakpoints.up('sm'));
  const openReader = usePulseReader();
  const transport = usePulseTransport();
  const { pulse: tracking } = useEventTracking();
  const pulse = usePulseController({
    transport,
    enabled: gate && desktop && Boolean(identityKey) && display.launcherVisible !== false,
    identityKey,
    isIdentityCurrent,
    suppressed: display.open,
    onOpenReader: openReader,
    onInteraction: action =>
      action === 'guide_clicked' ? tracking.guideClicked() : tracking.dismissClicked(),
  });
  const suggestion = pulse.suggestion;
  return (
    <JotSupportContainer
      {...display}
      renderLauncherAccessory={anchor =>
        suggestion && (
          <JotPulseBubble
            key={suggestion.pulse_id}
            {...anchor}
            anchored
            prompt={suggestion.prompt}
            articleTitle={suggestion.article.title}
            expiresAt={Date.parse(suggestion.expires_at)}
            dismissible={suggestion.dismissible}
            onShown={pulse.shown}
            onOpenGuide={pulse.openGuide}
            onDismiss={pulse.dismiss}
            onExpire={pulse.hide}
          />
        )
      }
    />
  );
}
